import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DataGrid, Column, Lookup, Editing, Scrolling, DataGridTypes } from 'devextreme-react/data-grid';
import { SelectBox, SelectBoxTypes } from 'devextreme-react/select-box';
import { Button } from 'devextreme-react/button';
import { formatMessage } from 'devextreme/localization';

import DataSourceApi from '../../api/DataSourceApi';
import FieldApi from '../../api/FieldApi';

import PathListDto from '../../classes/dtos/pathmodule/PathListDto';
import FieldReferenceIdAndKeyDto from '../../classes/dtos/FieldReferenceIdAndKeyDto';
import GroupFilterDto from '../../classes/dtos/datasourcemodule/GroupFilterDto';
import DataTableReadDto from '../../classes/dtos/pathcommonmodule/DataTableReadDto';
import PathDataTableReadDto from '../../classes/dtos/pathcommonmodule/PathDataTableReadDto';
import DataTableIterationLinkDto from '../../classes/dtos/pathcommonmodule/DataTableIterationLinkDto';

import { collectParameterOptions, loadFilteringCapabilities } from '../filter-selection/FilterFieldsShared';

interface IterationDatasetPanelProps {
    // Table source courante : sert à résoudre le type de chaque variable (pour filtrer les colonnes liables).
    TableReferenceId: number;
    // Filtre courant (live) : source de la liste des paramètres. Seules les variables réellement
    // référencées par une condition « Variable » de l'arbre du filtre complexe deviennent des lignes.
    Filter: GroupFilterDto | null;
    // Path DataTable actuellement choisi comme jeu de données d'itération.
    Iteration: DataTableReadDto | null;
    IterationLinks: DataTableIterationLinkDto[];
    // Commit combiné unique : Iteration ET IterationLinks poussés en un seul appel (voir handlePathChanged).
    OnChange(iteration: DataTableReadDto | null, iterationLinks: DataTableIterationLinkDto[]): void;
}

type LinkStatus = 'linked' | 'column-missing' | 'unlinked';

// Une ligne = un paramètre (variable) du filtre. On choisit la colonne du Path DataTable qui le remplira.
interface ParamRow {
    TechnicalName: string;
    Name: string;
    VariableType: number | null;   // type du champ que porte la variable ; null si non résolu
    LinkedColumnId: number | null;
    LinkedColumnName: string | null;
    Status: LinkStatus;
}

interface ColumnItem {
    Id: number;
    Label: string;
    TypeNum: number;
}

const MUTED = '#9393A2';
const HEADING = '#E3E3E8';
const STROKE = '#3D3D46';

const STATUS_COLOR: Record<LinkStatus, string> = {
    'linked': '#4CAF7D',
    'column-missing': '#D9A441',
    'unlinked': MUTED,
};

// Panneau droit de l'onglet Filters, dédié à l'itération : on choisit un Path DataTable dont chaque ligne
// alimentera les paramètres du path, puis pour chaque paramètre du filtre on choisit la colonne qui le
// remplit. Les liens sont dérivés des props (aucun état local) : chaque édition remonte via OnChange.
const IterationDatasetPanel: React.FC<IterationDatasetPanelProps> = ({ TableReferenceId, Filter, Iteration, IterationLinks, OnChange }) => {

    const [paths, setPaths] = useState<PathListDto[]>([]);
    const [pathsLoaded, setPathsLoaded] = useState(false);

    const [columns, setColumns] = useState<FieldReferenceIdAndKeyDto[]>([]);
    const [columnsLoading, setColumnsLoading] = useState(false);

    // Type (FieldType numérique) de chaque champ filtrable de la table source, par FieldReferenceId :
    // sert à résoudre le type des variables du filtre pour filtrer les colonnes liables.
    const [fieldTypesByKey, setFieldTypesByKey] = useState<Record<string, number>>({});

    const selectedPathId = Iteration instanceof PathDataTableReadDto ? Iteration.PathId : null;
    const selectedPathName = Iteration instanceof PathDataTableReadDto ? Iteration.PathName : null;

    // La liste des Paths est chargée en anticipé ; en attendant, on injecte le Path déjà sélectionné (nom
    // porté par l'Iteration) pour que son libellé s'affiche même liste fermée.
    const selectBoxItems = useMemo<Array<{ Id: number; Name: string }>>(() => {
        if (selectedPathId != null && !paths.some((p) => p.Id === selectedPathId)) {
            return [{ Id: selectedPathId, Name: selectedPathName ?? String(selectedPathId) }, ...paths];
        }
        return paths;
    }, [paths, selectedPathId, selectedPathName]);

    const loadPathsIfNeeded = useCallback(() => {
        if (pathsLoaded) {
            return;
        }
        // Mêmes arguments que SourceSelection en Direction "Source" (readable=true, writable=null).
        DataSourceApi.GetDataSourcesByCustomerAsync(true, null).then((result) => {
            if (result.IsFailed()) {
                return; // pas de setPathsLoaded : on réessaiera à la prochaine ouverture
            }
            const list = [...result.Result.PathDataTables].sort((a, b) =>
                a.Name.toLowerCase() < b.Name.toLowerCase() ? -1 : a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : 0);
            setPaths(list);
            setPathsLoaded(true);
        });
    }, [pathsLoaded]);

    useEffect(() => {
        loadPathsIfNeeded();
    }, [loadPathsIfNeeded]);

    useEffect(() => {
        if (selectedPathId == null) {
            setColumns([]);
            return;
        }

        let cancelled = false;
        setColumnsLoading(true);
        FieldApi.GetComputedFieldsAsync(null, selectedPathId).then((result) => {
            if (cancelled) {
                return;
            }
            setColumns(result.IsFailed() ? [] : result.Result.Fields);
            setColumnsLoading(false);
        });

        return () => { cancelled = true; };
    }, [selectedPathId]);

    useEffect(() => {
        if (TableReferenceId <= 0) {
            setFieldTypesByKey({});
            return;
        }
        let cancelled = false;
        loadFilteringCapabilities(TableReferenceId).then(({ fieldTypesByKey: typesByKey }) => {
            if (!cancelled) {
                setFieldTypesByKey(typesByKey);
            }
        });
        return () => { cancelled = true; };
    }, [TableReferenceId]);

    const resolveType = useCallback((fieldReferenceId: number) => fieldTypesByKey[fieldReferenceId.toString()], [fieldTypesByKey]);

    // Variables liables : uniquement celles réellement référencées par une condition « Variable » de
    // l'arbre du filtre complexe courant. Les variables déclarées dans les grilles de mapping mais non
    // encore utilisées dans le filtre n'apparaissent pas ici. Filter est reconstruit en objet neuf par
    // FiltersSelection à chaque édition -> recalcul live.
    const paramOptions = useMemo(
        () => collectParameterOptions(Filter, resolveType),
        [Filter, resolveType]
    );

    // Liens effectifs : on écarte tout lien dont le paramètre n'est plus une variable du filtre complexe
    // courant (« lien cassé »). On ne les conserve pas « au cas où la condition reviendrait » — la prune
    // définitive côté persistance est faite par FiltersSelection.handleFilterChange à chaque édition du
    // filtre ; ici c'est le miroir pour l'affichage et les commits du panneau.
    const effectiveIterationLinks = useMemo(
        () => IterationLinks.filter((l) => paramOptions.some((o) => o.TechnicalName === l.ParameterTechnicalName)),
        [IterationLinks, paramOptions]
    );

    // Colonnes du Path DataTable aplaties pour le Lookup : Id + libellé + type numérique (pour le filtre par type).
    const columnItems: ColumnItem[] = useMemo(() => columns.map((c) => ({
        Id: c.Id,
        Label: `${c.Name} (${c.Type.OriginTypeLabel ?? '???'})`,
        TypeNum: c.Type.Type,
    })), [columns]);

    const rows: ParamRow[] = useMemo(() => paramOptions.map((option) => {
        const link = effectiveIterationLinks.find((l) => l.ParameterTechnicalName === option.TechnicalName) ?? null;
        const column = link != null ? columns.find((c) => c.Id === link.FieldReferenceId) ?? null : null;
        const status: LinkStatus = link == null ? 'unlinked' : (column != null ? 'linked' : 'column-missing');
        return {
            TechnicalName: option.TechnicalName,
            Name: option.Name,
            VariableType: option.FieldType,
            LinkedColumnId: link?.FieldReferenceId ?? null,
            LinkedColumnName: column?.Name ?? null,
            Status: status,
        };
    }), [paramOptions, effectiveIterationLinks, columns]);

    // dataSource du Lookup filtré par ligne : ne propose que les colonnes du même type que la variable
    // (+ la colonne déjà liée, même si son type ne correspond plus). Type non résolu -> toutes les colonnes.
    const linkedColumnDataSource = useCallback((options: { data?: Record<string, unknown> }) => {
        const row = options.data as ParamRow | undefined;
        if (row == null || row.VariableType == null) {
            return { store: columnItems };
        }
        const sameType: unknown[] = ['TypeNum', '=', row.VariableType];
        const filter: unknown[] = row.LinkedColumnId != null ? [sameType, 'or', ['Id', '=', row.LinkedColumnId]] : sameType;
        return { store: columnItems, filter };
    }, [columnItems]);

    const handlePathChanged = useCallback((e: SelectBoxTypes.ValueChangedEvent) => {
        const newPathId = (e.value ?? null) as number | null;
        // La SelectBox contrôlée réémet valueChanged quand le parent re-render avec la même Iteration :
        // sans ce garde, on réappellerait OnChange(mêmePath, []) et on effacerait les liens à chaque rendu.
        if (newPathId === selectedPathId) {
            return;
        }
        const path = newPathId != null ? paths.find((p) => p.Id === newPathId) ?? null : null;
        // Commit combiné : on ne peut pas scinder "poser Iteration" puis "vider les liens".
        OnChange(path != null ? new PathDataTableReadDto(path.Id, path.Name) : null, []);
    }, [paths, selectedPathId, OnChange]);

    const clearDataTable = useCallback(() => OnChange(null, []), [OnChange]);

    const handleRowUpdated = useCallback((e: DataGridTypes.RowUpdatedEvent) => {
        const changedKey = e.key as string;
        const changedColumnId = ((e.data as ParamRow).LinkedColumnId) ?? null;

        // On repart uniquement des lignes visibles (= variables du filtre complexe courant). Un lien dont
        // le paramètre a quitté le filtre n'est pas réinjecté : il disparaît.
        const links = rows
            .map((r) => {
                const columnId = r.TechnicalName === changedKey ? changedColumnId : r.LinkedColumnId;
                if (columnId == null) {
                    return null;
                }
                const option = paramOptions.find((o) => o.TechnicalName === r.TechnicalName);
                return option != null ? new DataTableIterationLinkDto(columnId, option.Id, option.TechnicalName) : null;
            })
            .filter((x): x is DataTableIterationLinkDto => x !== null);

        OnChange(Iteration, links);
    }, [rows, paramOptions, Iteration, OnChange]);

    const renderStatus = useCallback((cell: DataGridTypes.ColumnCellTemplateData) => {
        const status = (cell.data as ParamRow).Status;
        const label = status === 'linked'
            ? formatMessage('IterationDataset_StatusLinked')
            : status === 'column-missing'
                ? formatMessage('IterationDataset_StatusColumnMissing')
                : formatMessage('IterationDataset_StatusUnlinked');
        return <span style={{ color: STATUS_COLOR[status], fontWeight: 600, fontSize: 12 }}>{label}</span>;
    }, []);

    const linkedColumnIds = useMemo(() => new Set(effectiveIterationLinks.map((l) => l.FieldReferenceId)), [effectiveIterationLinks]);
    const unlinkedColumnsCount = columns.filter((c) => !linkedColumnIds.has(c.Id)).length;

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ color: HEADING, fontSize: 16, fontFamily: 'Manrope', fontWeight: 600 }}>
                    {formatMessage('IterationDataset_Title')}
                </div>
                <div style={{ color: MUTED, fontSize: 12, fontFamily: 'Manrope', fontWeight: 500 }}>
                    {formatMessage('IterationDataset_Description')}
                </div>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <SelectBox
                        items={selectBoxItems}
                        displayExpr="Name"
                        valueExpr="Id"
                        searchEnabled={true}
                        value={selectedPathId}
                        placeholder={formatMessage('ParametersPanel_SelectPath')}
                        onOpened={loadPathsIfNeeded}
                        onValueChanged={handlePathChanged}
                    />
                </div>
                <Button
                    icon="trash"
                    stylingMode="text"
                    visible={selectedPathId != null}
                    hint={formatMessage('IterationDataset_ClearDataTable')}
                    onClick={clearDataTable}
                />
            </div>

            {selectedPathId == null ? (
                <div style={{ color: MUTED, fontSize: 14, fontFamily: 'Manrope', fontWeight: 500 }}>
                    {formatMessage('IterationDataset_NoDataTable')}
                </div>
            ) : columnsLoading ? (
                <div style={{ color: MUTED, fontSize: 14, fontFamily: 'Manrope', fontWeight: 500 }}>
                    {formatMessage('IterationDataset_LoadingColumns')}
                </div>
            ) : columns.length === 0 ? (
                <div style={{ color: MUTED, fontSize: 14, fontFamily: 'Manrope', fontWeight: 500 }}>
                    {formatMessage('IterationDataset_NoColumns')}
                </div>
            ) : paramOptions.length === 0 ? (
                <div style={{ color: MUTED, fontSize: 14, fontFamily: 'Manrope', fontWeight: 500 }}>
                    {formatMessage('IterationDataset_NoParameters')}
                </div>
            ) : (
                <>
                    <IterationSchematic rows={rows} unlinkedColumnsCount={unlinkedColumnsCount} datasetName={selectedPathName} />
                    <DataGrid
                        dataSource={rows}
                        keyExpr="TechnicalName"
                        showBorders={true}
                        columnAutoWidth={true}
                        repaintChangesOnly={true}
                        height="auto"
                        onRowUpdated={handleRowUpdated}
                    >
                        <Scrolling mode="standard" />
                        <Editing mode="cell" allowUpdating={true} allowAdding={false} allowDeleting={false} />
                        <Column dataField="Name" caption={formatMessage('IterationDataset_ParamHeader')} allowEditing={false} />
                        <Column
                            dataField="LinkedColumnId"
                            caption={formatMessage('IterationDataset_LinkedColumnHeader')}
                            allowEditing={true}
                        >
                            <Lookup dataSource={linkedColumnDataSource} valueExpr="Id" displayExpr="Label" allowClearing={true} />
                        </Column>
                        <Column
                            dataField="Status"
                            caption={formatMessage('IterationDataset_StatusHeader')}
                            allowEditing={false}
                            width={130}
                            allowFiltering={false}
                            allowSorting={false}
                            cellRender={renderStatus}
                        />
                    </DataGrid>
                </>
            )}
        </div>
    );
};

// Schéma du principe : les paires paramètre <- colonne effectivement liées, + le nombre de colonnes non
// liées, + "1 ligne du jeu de données = 1 exécution du path".
const IterationSchematic: React.FC<{ rows: ParamRow[]; unlinkedColumnsCount: number; datasetName: string | null }> = ({ rows, unlinkedColumnsCount, datasetName }) => {
    const MAX = 5;

    const pairs = rows
        .filter((r) => r.LinkedColumnId != null)
        .map((r) => ({
            paramName: r.Name,
            columnName: r.LinkedColumnName ?? '?',
            missing: r.Status === 'column-missing',
        }));

    const shown = pairs.slice(0, MAX);
    const extra = pairs.length - shown.length;

    const clip = (s: string) => (s.length > 14 ? s.slice(0, 13) + '…' : s);
    const rowY = (i: number) => 24 + i * 26;
    const height = 46 + Math.max(shown.length, 1) * 26 + (extra > 0 ? 16 : 0);
    const caption = (datasetName != null ? `« ${datasetName} » : ` : '') + formatMessage('IterationDataset_SchematicRows');

    return (
        <svg viewBox={`0 0 280 ${height}`} style={{ width: '100%', height: 'auto' }} role="img"
            aria-label={formatMessage('IterationDataset_Title')}>
            <text x="8" y="12" fill={MUTED} fontSize={8} fontFamily="Manrope" fontWeight={700}
                style={{ letterSpacing: 1 }}>{formatMessage('IterationDataset_SchematicVariables').toUpperCase()}</text>
            <text x="272" y="12" fill={MUTED} fontSize={8} fontFamily="Manrope" fontWeight={700} textAnchor="end"
                style={{ letterSpacing: 1 }}>{formatMessage('IterationDataset_SchematicColumns').toUpperCase()}</text>

            {shown.length === 0 ? (
                <text x="140" y={rowY(0) + 3} fill={MUTED} fontSize={9} fontFamily="Manrope" textAnchor="middle">
                    {formatMessage('IterationDataset_SchematicHint')}
                </text>
            ) : shown.map((p, i) => (
                <g key={`${p.paramName}-${i}`}>
                    <rect x="8" y={rowY(i) - 10} width="104" height="20" rx="4" fill="#2A2A32" stroke={STROKE} />
                    <text x="16" y={rowY(i) + 3} fill={HEADING} fontSize={9} fontFamily="Manrope">{clip(p.paramName)}</text>

                    <line x1="112" y1={rowY(i)} x2="168" y2={rowY(i)}
                        stroke={p.missing ? '#5A5A66' : MUTED} strokeWidth={1.5}
                        strokeDasharray={p.missing ? '3 3' : undefined} />

                    <rect x="168" y={rowY(i) - 10} width="104" height="20" rx="4" fill="#2A2A32" stroke={STROKE} />
                    <text x="264" y={rowY(i) + 3} fill={p.missing ? MUTED : HEADING} fontSize={9} fontFamily="Manrope" textAnchor="end">{clip(p.columnName)}</text>
                </g>
            ))}

            {extra > 0 && (
                <text x="16" y={rowY(shown.length) + 3} fill={MUTED} fontSize={9} fontFamily="Manrope">+{extra}</text>
            )}

            <text x="8" y={height - 20} fill={MUTED} fontSize={9} fontFamily="Manrope">
                {unlinkedColumnsCount} {formatMessage('IterationDataset_SchematicUnlinkedColumns')}
            </text>
            <text x="140" y={height - 5} fill={MUTED} fontSize={9} fontFamily="Manrope" textAnchor="middle">
                {caption}
            </text>
        </svg>
    );
};

export default IterationDatasetPanel;
